/**
 * CR225: Single Source of Truth for Card Visibility
 * 
 * This module provides the ONLY function that computes visibility flags
 * for DealGiftCard. All render paths must read from EffectiveVisibility.
 * 
 * Do NOT check styling.showX, template.sections.X, or attributeVisibility
 * directly in the renderer. All visibility logic is centralized here.
 * 
 * IMPORTANT: This module now reads from a single `visibility` object,
 * NOT from both styling and sections. The unified CardVisibility type
 * is the ONLY source of truth.
 */

import type { MerchantNamePlacement, MerchantNameStyle } from "./globalThemeTokens";
import { 
  type CardVisibility, 
  DEFAULT_VISIBILITY, 
  computeEffectiveVisibility,
  migrateToCardVisibility,
  type EffectiveVisibility,
  type VisibilityContext,
} from "./cardVisibility";

// Re-export for convenience
export type { CardVisibility, EffectiveVisibility, VisibilityContext };
export { DEFAULT_VISIBILITY, computeEffectiveVisibility, migrateToCardVisibility };

// ================================================================
// LEGACY SUPPORT: VisibilityInputs (for backward compatibility)
// ================================================================

/**
 * @deprecated Use CardVisibility directly instead.
 * This is kept for backward compatibility during migration.
 */
export interface VisibilityInputs {
  /** From templateOverride?.styling */
  styling?: {
    showMerchantLogo?: boolean;
    showMerchantName?: boolean;
    showRibbon?: boolean;
    showStarburst?: boolean;
  };
  /** From templateOverride?.sections or extended_sections */
  sections?: {
    merchant_name?: boolean;
    ribbon?: boolean;
    headline?: boolean;
    dates?: boolean;
    promo_badges?: boolean;
    details_pills?: boolean;
    merchant_category_chips?: boolean;
    confidence_indicator?: boolean;
    claim_button?: boolean;
    debug_strip?: boolean;
    effective_value_box?: boolean;
    qualifying_spend_box?: boolean;
    reward_box?: boolean;
    eligibility_box?: boolean;
    source_buttons?: boolean;
    why_deal_accordion?: boolean;
  };
  /** From global theme tokens */
  globalTokens?: {
    components?: {
      merchantName?: {
        placement?: MerchantNamePlacement;
        style?: MerchantNameStyle;
      };
    };
  };
  /** Surface context - may affect defaults */
  surface?: "preview" | "admin" | "public";
  /** Is this an admin context? */
  isAdmin?: boolean;
}

/**
 * @deprecated Use computeEffectiveVisibility with CardVisibility directly.
 * This function exists for backward compatibility and migrates legacy inputs.
 */
export function getEffectiveVisibility(inputs: VisibilityInputs): EffectiveVisibility {
  // Migrate legacy styling + sections to unified CardVisibility
  const visibility = migrateToCardVisibility(inputs.styling, inputs.sections);
  
  const context: VisibilityContext = {
    globalTokens: inputs.globalTokens,
    surface: inputs.surface,
    isAdmin: inputs.isAdmin,
  };
  
  return computeEffectiveVisibility(visibility, context);
}

/**
 * Create a debug snapshot of the visibility state.
 * Used by the CR225 Visibility Self-Test panel.
 */
export function createVisibilityDebugSnapshot(
  visibility: EffectiveVisibility,
  inputs: VisibilityInputs
): Record<string, unknown> {
  return {
    timestamp: new Date().toISOString(),
    computed: visibility,
    inputs: {
      hasStyling: !!inputs.styling,
      hasSections: !!inputs.sections,
      hasGlobalTokens: !!inputs.globalTokens,
      surface: inputs.surface,
      isAdmin: inputs.isAdmin,
    },
    stylingRaw: inputs.styling,
    sectionsRaw: inputs.sections,
  };
}
